import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Plus } from "lucide-react";

interface WeighStation {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  active: boolean;
  created_at: string;
}

export const AdminWeighStations = () => {
  const [stations, setStations] = useState<WeighStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const { data, error } = await supabase
          .from("weigh_stations")
          .select("*")
          .order("name", { ascending: true });

        if (error) throw error;
        setStations(data || []);
      } catch (error) {
        console.error("Error fetching weigh stations:", error);
        toast({
          title: "Error",
          description: "Failed to load weigh stations",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStations();
  }, []);

  const handleCreateStation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("weigh_stations")
        .insert([formData]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Weigh station created successfully",
      });

      setDialogOpen(false);
      setFormData({
        name: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        active: true,
      });
      
      const { data } = await supabase
        .from("weigh_stations")
        .select("*")
        .order("name", { ascending: true });
      
      setStations(data || []);
    } catch (error: any) {
      console.error("Error creating weigh station:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create weigh station",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading weigh stations...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          <h2 className="text-2xl font-bold">Weigh Stations ({stations.length})</h2>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Station
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Weigh Station</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateStation} className="space-y-4">
              <div>
                <Label htmlFor="name">Station Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP</Label>
                  <Input
                    id="zip"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="active">Active</Label>
              </div>
              <Button type="submit" className="w-full">Create Station</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {stations.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No weigh stations found
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {stations.map((station) => (
            <Card key={station.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {station.name}
                  <Badge variant={station.active ? 'default' : 'secondary'}>
                    {station.active ? 'Active' : 'Inactive'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Added {new Date(station.created_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {station.address && (
                    <div>
                      <strong>Address:</strong> {station.address}
                    </div>
                  )}
                  {station.city && station.state && (
                    <div>
                      <strong>Location:</strong> {station.city}, {station.state} {station.zip}
                    </div>
                  )}
                  {station.phone && (
                    <div>
                      <strong>Phone:</strong> {station.phone}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
